// eslint-disable-next-line import/no-unresolved
import brand from '@brand/brand.js';
import { Transition } from '@headlessui/react';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as z from 'zod';
import { ProgressLayout } from '../../components/layouts/progress-layout';
import { PatchInviteForm } from '../../components/patch-invite-form';
import { SubmitButton } from '../../components/submit-button';
import { Checkbox } from '../../components/ui/checkbox';
import { InfoBlock } from '../../components/ui/info-block';
import { Input } from '../../components/ui/input';
import { RadioCard, RadioGroup } from '../../components/ui/radio-group-list';
import { Select } from '../../components/ui/select';
import { yesNoDefaultValue } from '../../utils/misc';
import { cleanPhone } from '../../utils/phone';

const INSURER_OPTIONS = [
  { value: 'southern-cross', label: 'Southern Cross' },
  { value: 'nib', label: 'NIB' },
  { value: 'aia', label: 'AIA' },
];

const schema = z.union([
  z.object({
    termsAccepted: z.literal(true),
    hasCoverage: z.literal('no'),
  }),
  z.object({
    termsAccepted: z.literal(true),
    hasCoverage: z.literal('yes'),
    hasPreApproval: z.enum(['yes', 'no']),
    healthInsurer: z.string().nonempty(),
    membershipNumber: z.string().nonempty(),
  }),
]);

function extractDefaults({ insurance }) {
  return {
    termsAccepted: insurance?.termsAccepted ?? '',
    hasCoverage: yesNoDefaultValue(insurance?.hasCoverage) ?? 'no',
    hasPreApproval: yesNoDefaultValue(insurance?.hasPreApproval),
    membershipNumber: insurance?.membershipNumber ?? '',
    healthInsurer: insurance?.healthInsurer ?? '',
  };
}

function transformValues(values) {
  return {
    insurance: {
      ...values,
      hasCoverage: values.hasCoverage === 'yes',
      hasPreApproval: values.hasPreApproval === 'yes',
    },
  };
}

export function Insurance(props) {
  const { t } = useTranslation('page.insurance');
  const { t: tCommon } = useTranslation();

  const hasCoverageOptions = useMemo(
    () => [
      {
        value: 'yes',
        label: tCommon('yes'),
      },
      {
        value: 'no',
        label: `${tCommon('no')} - ${t('noCovered')}`,
      },
    ],
    [t, tCommon],
  );

  return (
    <PatchInviteForm
      nextPath={props.next.path}
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
    >
      <ProgressLayout {...props} showMenu={false}>
        <h1 className="title">{t('title')}</h1>
        <Select
          name="hasCoverage"
          label={t('insuranceCovered')}
          options={hasCoverageOptions}
          labelVariant="base"
          className="mb-5 last:mb-0"
        />
        <InsurerFields />
        <Checkbox name="termsAccepted" label={t('insuranceLabel')} className="pb-0 mb-0" />
        <Trans
          i18nKey="insuranceDetails"
          ns="page.insurance"
          components={{
            ul: <ul className="pl-14 mb-4.5 -mt-1.5 list-disc" />,
            li: <li />,
            strong: <strong className="font-semibold" />,
          }}
        />
        <SubmitButton />
        <InfoBlock>
          <Trans
            i18nKey="helpMessage"
            ns="page.insurance"
            values={{ phone: brand.phone }}
            components={{
              h3: <h3 className="subtitle mb-0" />,
              p: <p />,
              br: <br />,
              strong: <strong className="font-semibold" />,
              a: <a href={`tel:${cleanPhone(brand.phone)}`} />,
            }}
          />
        </InfoBlock>
      </ProgressLayout>
    </PatchInviteForm>
  );
}

function InsurerFields() {
  const { t } = useTranslation('page.insurance');
  const { t: tCommon } = useTranslation();
  const { watch, setValue } = useFormContext();
  const hasCoverage = watch('hasCoverage');
  const healthInsurer = watch('healthInsurer');
  const hasPreApproval = watch('hasPreApproval');
  const hasCovered = hasCoverage === 'yes';

  const hasPreApprovalOptions = useMemo(
    () => [
      {
        value: 'yes',
        label: tCommon('yes'),
      },
      {
        value: 'no',
        label: tCommon('no'),
      },
    ],
    [tCommon],
  );

  useEffect(() => {
    if (hasCoverage === 'no') {
      setValue('healthInsurer', '');
      setValue('membershipNumber', '');
      setValue('hasPreApproval', 'no');
    }
  }, [hasCoverage, setValue]);

  return (
    <>
      <Transition
        show={hasCovered}
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="space-y-4">
          <Select
            name="healthInsurer"
            options={INSURER_OPTIONS}
            ns="page.insurance"
            label={t('insurerLabel')}
            labelVariant="base"
            className="mb-5 last:mb-0"
          />
          <Input
            name="membershipNumber"
            label={t('membershipLabel')}
            type="number"
            labelVariant="base"
          />
          <RadioGroup
            orientation="horizontal"
            name="hasPreApproval"
            label={t('preApprovalLabel')}
            options={hasPreApprovalOptions}
            labelVariant="base"
            labelClassName="border-2"
          />
        </div>
      </Transition>

      <Transition
        show={hasCovered && healthInsurer !== '' && hasPreApproval === 'no'}
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="relative z-1 mt-6 py-5 before:absolute before:-z-1 before:top-0 before:bottom-0 before:left-[-99999px] before:right-[-99999px] before:bg-primary-50">
          <Trans
            i18nKey="preApprovalText"
            ns="page.insurance"
            components={{
              h3: <h3 className="subtitle mb-3.75" />,
              p: <p />,
              strong: <strong className="font-semibold" />,
              ul: <ul className="pl-6 list-disc" />,
              li: <li />,
              span: <p className="mb-0" />,
            }}
            values={{
              insurer: t(`insurerSelect.${healthInsurer}`),
            }}
          />
        </div>
      </Transition>
    </>
  );
}
